import { useMemo, useState } from 'react';
import { useActionReport, useAdminReports, useDismissReport } from '../useAdmin';
import type { AdminReportRow, ListReportsParams } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminReports.css';

const PAGE_SIZE = 25;

/**
 * Admin reports queue (F8). Lists open reports against gallery
 * submissions; the operator marks each as `dismissed` (false alarm)
 * or `actioned` (handled the underlying submission/user). The flag
 * `open_only` defaults to true so the queue stays focused.
 */
export function AdminReports(): JSX.Element {
  const [openOnly, setOpenOnly] = useState(true);
  const [page, setPage] = useState(0);

  const params = useMemo<ListReportsParams>(
    () => ({
      open_only: openOnly,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [openOnly, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminReports(params);
  const dismiss = useDismissReport();
  const action = useActionReport();
  const lastError = dismiss.error || action.error;

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  return (
    <section className="bl-admin-reports" aria-label="Reports queue">
      <header className="bl-admin-reports__header">
        <div>
          <h2>Reports</h2>
          <p className="bl-admin-reports__sub">
            User-submitted abuse signals against gallery posts. Mark each as dismissed (no problem)
            or actioned (you suspended / took down already).
          </p>
        </div>
        <p className="bl-admin-reports__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No reports match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form className="bl-admin-reports__filters" onSubmit={(e) => e.preventDefault()}>
        <label className="bl-admin-reports__field">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => {
              setOpenOnly(e.target.checked);
              setPage(0);
            }}
          />
          <span>Show only open reports</span>
        </label>
      </form>

      {(isError || lastError) && (
        <p className="bl-admin-reports__error" role="alert">
          {lastError instanceof AuthApiError
            ? lastError.detail || lastError.message
            : (error || lastError) instanceof Error
              ? ((error || lastError) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      <ul className="bl-admin-reports__list">
        {data?.rows.map((row) => (
          <ReportEntry
            key={row.id}
            row={row}
            onDismiss={() => {
              const note = window.prompt('Optional note (why is this not actionable?):', '');
              if (note === null) return;
              dismiss.mutate({ id: row.id, note: note.trim() || undefined });
            }}
            onAction={() => {
              const note = window.prompt(
                'What did you do? (suspended user / took down submission / etc.)',
                '',
              );
              if (note === null) return;
              action.mutate({ id: row.id, note: note.trim() || undefined });
            }}
            busy={dismiss.isPending || action.isPending}
          />
        ))}
        {!isLoading && data?.rows.length === 0 && (
          <li className="bl-admin-reports__empty">
            {openOnly
              ? 'Inbox zero. No open reports right now.'
              : 'No reports match the current filter.'}
          </li>
        )}
      </ul>

      <nav className="bl-admin-reports__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-reports__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function ReportEntry({
  row,
  onDismiss,
  onAction,
  busy,
}: {
  row: AdminReportRow;
  onDismiss: () => void;
  onAction: () => void;
  busy: boolean;
}): JSX.Element {
  const isOpen = row.disposition === null;
  return (
    <li className="bl-admin-reports__entry" data-disposition={row.disposition || 'open'}>
      <div className="bl-admin-reports__head">
        <span className="bl-admin-reports__reason">{row.reason}</span>
        {row.disposition && (
          <span className="bl-admin-reports__pill">
            {row.disposition} by {row.disposed_by_email || '(unknown)'}
          </span>
        )}
        <time className="bl-admin-reports__when" dateTime={row.created_at}>
          {formatDate(row.created_at)}
        </time>
      </div>
      <p className="bl-admin-reports__line">
        On submission <code className="bl-admin-reports__sid">{row.submission_id.slice(0, 8)}</code>
        {row.project_name && (
          <>
            {' · '}
            <span className="bl-admin-reports__project">{row.project_name}</span>
          </>
        )}
        {row.submitter_email && (
          <>
            {' by '}
            <span className="bl-admin-reports__submitter">{row.submitter_email}</span>
          </>
        )}
        {row.submission_status && (
          <>
            {' · status '}
            <span className="bl-admin-reports__status">
              {row.submission_status.replace(/_/g, ' ')}
            </span>
          </>
        )}
      </p>
      {row.detail && <p className="bl-admin-reports__detail">{row.detail}</p>}
      {row.disposed_note && <p className="bl-admin-reports__note">Note: {row.disposed_note}</p>}
      {isOpen && (
        <div className="bl-admin-reports__actions">
          <button type="button" onClick={onDismiss} disabled={busy}>
            Dismiss
          </button>
          <button
            type="button"
            onClick={onAction}
            disabled={busy}
            className="bl-admin-reports__action--primary"
          >
            Mark actioned
          </button>
        </div>
      )}
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
